import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import {
  ActionChip,
  ChevronRightIcon,
  LockIcon,
  PageHeader,
  PlusIcon,
  SectionCard,
  SettingsIcon,
} from '../components/layout/memberPrimitives';
import {
  DailyEntryRecord,
  TrackerSettingsRecord,
  DEFAULT_TRACKER_SETTINGS,
  BristolMovement,
  UnitSystem,
  TrackerSummaryRecord,
  EMPTY_TRACKER_SUMMARY,
  BadgeId,
  CustomPill,
} from '../types/dailyBite';
import {
  formatTimeOfDay,
  getDailyEntry,
  getTrackerSettings,
  getTrackerSummary,
  isEntryLocked,
  recomputeAndSaveSummary,
  saveTrackerSettings,
  timestampFromDateAndTime,
  timestampNow,
  todayKey,
  upsertDailyEntryField,
} from '../services/dailyBiteService';
import { BADGE_LOOKUP } from '../services/dailyBiteBadges';
import {
  listCheerleadNotes,
  markCheerleadRead,
  reconcileCheerleadCounts,
  syncPartnerViewsFromSummary,
} from '../services/partnerService';
import type { CheerleadNoteRecord } from '../types/partner';
import {
  computeAndSaveWeeklyReflection,
  dismissWeeklyReflection,
  getWeeklyReflection,
  reflectionTargetWeekId,
  shouldShowReflection,
  weekRangeLabel,
} from '../services/weeklyReflectionService';
import { computeAndSaveCorrelations } from '../services/correlationService';
import type { WeeklyReflectionRecord } from '../types/dailyBite';
import { Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const GRATITUDE_PROMPTS = [
  'What went well today?',
  'Who helped you today?',
  'What surprised you in a good way?',
  "What\u2019s one thing you\u2019re looking forward to?",
  "What did you do well today, even if only for a moment?",
];

function promptForDate(dateKey: string): string {
  let hash = 0;
  for (let i = 0; i < dateKey.length; i += 1) {
    hash = (hash * 31 + dateKey.charCodeAt(i)) >>> 0;
  }
  return GRATITUDE_PROMPTS[hash % GRATITUDE_PROMPTS.length];
}

/** Reference amounts — shown as helper chips under sliders, not tap-to-add. */
const WATER_REFERENCES: Array<{ label: string; ml: number }> = [
  { label: 'Small glass', ml: 200 },
  { label: 'Tall glass', ml: 350 },
  { label: 'Bottle', ml: 500 },
  { label: 'Large bottle', ml: 750 },
];

const PROTEIN_REFERENCES: Array<{ label: string; grams: number }> = [
  { label: '2 eggs', grams: 12 },
  { label: 'Greek yogurt', grams: 17 },
  { label: 'Protein shake', grams: 25 },
  { label: 'Chicken breast', grams: 35 },
  { label: 'Salmon fillet', grams: 30 },
  { label: 'Steak', grams: 40 },
];

const CAFFEINE_REFERENCES: Array<{ label: string; mg: number }> = [
  { label: 'Tea', mg: 40 },
  { label: 'Espresso', mg: 63 },
  { label: 'Matcha', mg: 70 },
  { label: 'Coffee', mg: 95 },
  { label: 'Energy drink', mg: 80 },
  { label: 'Pre-workout', mg: 200 },
];

const ALCOHOL_REFERENCES: Array<{ label: string; units: number }> = [
  { label: 'Spirit 25ml', units: 1.0 },
  { label: 'Bottle 5%', units: 1.7 },
  { label: 'Pint 4%', units: 2.3 },
  { label: 'Wine 175ml', units: 2.3 },
  { label: 'Cocktail', units: 2.4 },
];

const MOOD_TAGS = ['focused', 'calm', 'energised', 'anxious', 'foggy', 'sociable', 'withdrawn', 'irritable', 'tired'];

const SLIDER_STYLE = `
.melio-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 8px;
  border-radius: 999px;
  outline: none;
  cursor: pointer;
  transition: background 0.1s linear;
}
.melio-slider:focus-visible {
  box-shadow: 0 0 0 3px rgba(79, 93, 24, 0.35);
}
.melio-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: #ffffff;
  border: 2px solid var(--melio-olive-deep);
  cursor: grab;
  box-shadow: 0 2px 5px rgba(31, 42, 30, 0.18);
  margin-top: -7px;
}
.melio-slider::-webkit-slider-thumb:active { cursor: grabbing; }
.melio-slider::-webkit-slider-runnable-track {
  height: 8px;
  border-radius: 999px;
  background: transparent;
}
.melio-slider::-moz-range-thumb {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: #ffffff;
  border: 2px solid var(--melio-olive-deep);
  cursor: grab;
  box-shadow: 0 2px 5px rgba(31, 42, 30, 0.18);
}
.melio-slider::-moz-range-track {
  height: 8px;
  border-radius: 999px;
  background: transparent;
}
`;

const BRISTOL_STOOL = [
  { value: 1, label: 'Hard lumps', hint: 'Separate pellets' },
  { value: 2, label: 'Lumpy', hint: 'Sausage-shaped, lumpy' },
  { value: 3, label: 'Cracked', hint: 'Sausage with cracks' },
  { value: 4, label: 'Smooth', hint: 'Smooth and soft' },
  { value: 5, label: 'Soft blobs', hint: 'Soft with clear edges' },
  { value: 6, label: 'Mushy', hint: 'Fluffy pieces' },
  { value: 7, label: 'Watery', hint: 'No solid pieces' },
];

function formatMl(ml: number, unitSystem: UnitSystem): string {
  if (unitSystem === 'imperial') {
    const flOz = ml / 29.5735;
    return `${flOz.toFixed(1)} fl oz`;
  }
  return `${ml} ml`;
}

function formatGrams(g: number, unitSystem: UnitSystem): string {
  if (unitSystem === 'imperial') {
    const oz = g / 28.3495;
    return `${oz.toFixed(1)} oz`;
  }
  return `${g} g`;
}

function moodLabelFor(valence: number, energy: number): string {
  if (energy > 0.25 && valence > 0.25) return 'Energised & positive';
  if (energy > 0.25 && valence < -0.25) return 'Energised but off';
  if (energy < -0.25 && valence > 0.25) return 'Calm & positive';
  if (energy < -0.25 && valence < -0.25) return 'Low energy, low mood';
  return 'Steady';
}

interface TrackerCardProps {
  tracker: keyof TrackerSettingsRecord['optedIn'];
  title: string;
  summary: string;
  logged: boolean;
  locked: boolean;
  children: React.ReactNode;
  eyebrow?: string;
  privacyNote?: string;
}

function TrackerCard({ title, summary, logged, locked, children, eyebrow, privacyNote }: TrackerCardProps) {
  return (
    <section
      className="rounded-2xl border p-5 md:p-6 bg-white"
      style={{ borderColor: 'var(--melio-border)' }}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          {eyebrow ? (
            <p className="text-[10px] uppercase tracking-[0.2em] mb-1" style={{ color: 'var(--melio-olive-deep)' }}>
              {eyebrow}
            </p>
          ) : null}
          <h3
            className="text-xl font-normal leading-tight"
            style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-ink)' }}
          >
            {title}
          </h3>
          <p className="text-sm mt-1" style={{ color: 'var(--melio-text-muted)' }}>{summary}</p>
          {privacyNote ? (
            <p className="text-[11px] mt-1" style={{ color: 'var(--melio-text-muted)' }}>
              <LockIcon size={11} className="inline-block mr-1 -mt-0.5" />
              {privacyNote}
            </p>
          ) : null}
        </div>
        {logged ? (
          <span
            className="text-[10px] uppercase tracking-wide px-2 py-1 rounded-full shrink-0"
            style={{ backgroundColor: 'rgba(79, 93, 24, 0.18)', color: 'var(--melio-olive-deep)' }}
          >
            Logged
          </span>
        ) : null}
      </div>
      <div style={{ opacity: locked ? 0.55 : 1, pointerEvents: locked ? 'none' : 'auto' }}>
        {children}
      </div>
    </section>
  );
}

/**
 * "Last at" time row — shown under time-sensitive sliders (caffeine, alcohol,
 * nicotine). Auto-stamped on slider commit; the member can correct the time
 * via a native time input. Used to drive phase-3 correlation analyses
 * (e.g. caffeine ≤6h before bed ↔ sleep quality, alcohol timing ↔ HRV).
 */
function LastAtRow({
  label,
  timestamp,
  onChange,
  dateKey,
  hint,
}: {
  label: string;
  timestamp: Timestamp | null | undefined;
  onChange: (next: Timestamp | null) => void;
  dateKey: string;
  hint?: string;
}) {
  const timeValue = formatTimeOfDay(timestamp);
  return (
    <div className="flex items-center gap-2 mt-3 flex-wrap">
      <span className="text-[11px] uppercase tracking-widest" style={{ color: 'var(--melio-text-muted)' }}>
        {label}
      </span>
      <input
        type="time"
        value={timeValue}
        onChange={(e) => {
          const next = e.target.value;
          if (!next) {
            onChange(null);
          } else {
            onChange(timestampFromDateAndTime(dateKey, next));
          }
        }}
        className="rounded-lg border px-2 py-1 text-sm"
        style={{
          borderColor: 'var(--melio-border)',
          backgroundColor: 'var(--melio-cream-warm)',
          color: 'var(--melio-ink)',
        }}
      />
      {timestamp ? (
        <button
          type="button"
          onClick={() => onChange(timestampNow())}
          className="text-[11px] underline"
          style={{ color: 'var(--melio-text-muted)' }}
        >
          now
        </button>
      ) : null}
      {hint ? (
        <span className="text-[11px] basis-full" style={{ color: 'var(--melio-text-muted)' }}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Pill row — each pill represents a real-world serving. Tapping a pill ADDS
 * its amount to the current slider value so the member can stack servings
 * ("2 coffees today" = tap twice). Custom pills (saved by the member) sit
 * alongside the built-in references. A trailing "+" opens the add-pill modal.
 */
function PillRow({
  items,
  onTap,
  onAddCustom,
  onRemoveCustom,
}: {
  items: Array<{ label: string; display: string; amount: number; isCustom?: boolean; id?: string }>;
  onTap: (amount: number) => void;
  onAddCustom?: () => void;
  onRemoveCustom?: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-3">
      {items.map((item) => (
        <PillButton
          key={item.id ?? item.label}
          label={item.label}
          display={item.display}
          isCustom={item.isCustom}
          onClick={() => onTap(item.amount)}
          onRemove={
            item.isCustom && item.id && onRemoveCustom ? () => onRemoveCustom(item.id!) : undefined
          }
        />
      ))}
      {onAddCustom ? (
        <button
          type="button"
          onClick={onAddCustom}
          className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] border transition-colors"
          style={{
            borderColor: 'rgba(79, 93, 24, 0.45)',
            backgroundColor: 'transparent',
            color: 'var(--melio-olive-deep)',
          }}
        >
          <PlusIcon size={10} />
          <span>Add your own</span>
        </button>
      ) : null}
    </div>
  );
}

function PillButton({
  label,
  display,
  isCustom,
  onClick,
  onRemove,
}: {
  label: string;
  display: string;
  isCustom?: boolean;
  onClick: () => void;
  onRemove?: () => void;
}) {
  const [hover, setHover] = useState(false);

  return (
    <span
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="inline-flex items-center rounded-full text-[11px] border overflow-hidden"
      style={{
        borderColor: isCustom ? 'rgba(79, 93, 24, 0.35)' : 'rgba(31, 42, 30, 0.08)',
        backgroundColor: isCustom ? 'rgba(79, 93, 24, 0.08)' : 'rgba(239, 238, 233, 0.6)',
      }}
    >
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 transition-colors"
        style={{ color: 'var(--melio-text-muted)' }}
      >
        <span style={{ color: 'var(--melio-ink)' }}>{label}</span>
        <span>+{display}</span>
      </button>
      {onRemove && hover ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label={`Remove ${label}`}
          className="inline-flex items-center justify-center pr-2 pl-1"
          style={{ color: 'var(--melio-text-muted)' }}
        >
          ×
        </button>
      ) : null}
    </span>
  );
}

/**
 * Pop-over modal used to save a custom pill (e.g. "My protein shake" · 42 g).
 * Tracker context drives the unit label so the input stays self-explanatory.
 * No slider here by design — pills are for quick, known servings, not for
 * guess-work logging.
 */
function AddPillModal({
  tracker,
  unitSystem,
  onClose,
  onSave,
}: {
  tracker: 'water' | 'protein' | 'caffeine';
  unitSystem: UnitSystem;
  onClose: () => void;
  onSave: (pill: { label: string; amount: number }) => Promise<void> | void;
}) {
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const config = {
    water: {
      title: 'Add a custom water serving',
      description: 'Save a bottle, glass or tumbler you use often. Tap it on the day to log it fast.',
      unitLabel: unitSystem === 'imperial' ? 'fluid ounces' : 'millilitres',
      placeholder: unitSystem === 'imperial' ? 'e.g. 16' : 'e.g. 750',
      toCanonical: (n: number) => (unitSystem === 'imperial' ? Math.round(n * 29.5735) : Math.round(n)),
    },
    protein: {
      title: 'Add a custom protein source',
      description: 'Save a meal or shake with a protein count you know. Tap it on the day to log it fast.',
      unitLabel: 'grams',
      placeholder: 'e.g. 42',
      toCanonical: (n: number) => Math.round(n),
    },
    caffeine: {
      title: 'Add a custom caffeine drink',
      description: 'Save a coffee, tea or energy drink with a milligram value you trust. Tap it on the day to log it fast.',
      unitLabel: 'milligrams',
      placeholder: 'e.g. 120',
      toCanonical: (n: number) => Math.round(n),
    },
  }[tracker];

  const submit = async () => {
    const trimmed = label.trim();
    const numeric = Number(amount);
    if (!trimmed) {
      setError('Give it a name.');
      return;
    }
    if (!Number.isFinite(numeric) || numeric <= 0) {
      setError('Enter a positive amount.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave({ label: trimmed, amount: config.toCanonical(numeric) });
    } catch {
      setError('Could not save. Try again.');
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(31, 42, 30, 0.45)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-full max-w-md rounded-3xl border p-6"
        style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--melio-cream-warm)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <p
          className="text-[10px] uppercase tracking-[0.22em] mb-2"
          style={{ color: 'var(--melio-olive-deep)' }}
        >
          Custom pill
        </p>
        <h3
          className="text-xl mb-1"
          style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-ink)' }}
        >
          {config.title}
        </h3>
        <p className="text-xs mb-4" style={{ color: 'var(--melio-text-muted)' }}>
          {config.description}
        </p>

        <label className="block text-xs mb-1" style={{ color: 'var(--melio-text-muted)' }}>
          Name
        </label>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="My protein shake"
          autoFocus
          className="w-full rounded-xl border px-3 py-2 text-sm mb-3"
          style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}
          maxLength={40}
        />

        <label className="block text-xs mb-1" style={{ color: 'var(--melio-text-muted)' }}>
          Amount ({config.unitLabel})
        </label>
        <input
          type="number"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder={config.placeholder}
          className="w-full rounded-xl border px-3 py-2 text-sm mb-4"
          style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}
        />

        {error ? (
          <p className="text-xs mb-3" style={{ color: 'var(--melio-terracotta-deep)' }}>
            {error}
          </p>
        ) : null}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center rounded-full px-4 py-2 text-sm"
            style={{ color: 'var(--melio-text-muted)' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="inline-flex items-center rounded-full px-4 py-2 text-sm melio-lift"
            style={{
              backgroundColor: 'var(--melio-olive-deep)',
              color: 'white',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Save pill'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Controlled slider with local state while dragging and a commit callback on
 * pointer/key release. Keeps writes off the hot path of every pixel.
 */
function TrackerSlider({
  value,
  min,
  max,
  step,
  onCommit,
  format,
  leftLabel,
  rightLabel,
}: {
  value: number;
  min: number;
  max: number;
  step: number;
  onCommit: (next: number) => void;
  format: (v: number) => string;
  leftLabel?: string;
  rightLabel?: string;
}) {
  const [local, setLocal] = useState(value);
  const committedRef = useRef(value);

  // Tween the thumb when `value` changes externally (e.g. a pill tap). Native
  // <input type="range"> won't animate its thumb on its own, so we step the
  // local value via rAF. Skips when the value matches what we just committed
  // (the round-trip after a drag).
  useEffect(() => {
    const from = committedRef.current;
    const to = value;
    if (from === to) {
      setLocal(to);
      return;
    }
    const duration = 260;
    const t0 = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const next = from + (to - from) * eased;
      setLocal(next);
      if (t < 1) raf = requestAnimationFrame(tick);
      else {
        setLocal(to);
        committedRef.current = to;
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);

  const commit = () => {
    if (local !== committedRef.current) {
      committedRef.current = local;
      onCommit(local);
    }
  };

  const pct = ((local - min) / (max - min)) * 100;
  const fillStyle = {
    background: `linear-gradient(to right, var(--melio-olive-deep) 0%, var(--melio-olive-deep) ${pct}%, rgba(31,42,30,0.12) ${pct}%, rgba(31,42,30,0.12) 100%)`,
  } as React.CSSProperties;

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <span
          className="text-2xl"
          style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-ink)' }}
        >
          {format(local)}
        </span>
        {rightLabel ? (
          <span className="text-[11px] uppercase tracking-widest" style={{ color: 'var(--melio-text-muted)' }}>
            {rightLabel}
          </span>
        ) : null}
      </div>
      <input
        type="range"
        value={local}
        min={min}
        max={max}
        step={step}
        onChange={(e) => setLocal(Number(e.target.value))}
        onPointerUp={commit}
        onTouchEnd={commit}
        onKeyUp={commit}
        onBlur={commit}
        className="melio-slider w-full"
        style={fillStyle}
        aria-label={leftLabel}
      />
      <div className="flex justify-between text-[10px] mt-1" style={{ color: 'var(--melio-text-muted)' }}>
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </div>
    </div>
  );
}

export default function DailyBitePage() {
  const { currentUser } = useAuth();
  const [dateKey] = useState(() => todayKey());
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<TrackerSettingsRecord>(DEFAULT_TRACKER_SETTINGS);
  const [entry, setEntry] = useState<DailyEntryRecord | null>(null);
  const [summary, setSummary] = useState<TrackerSummaryRecord>(EMPTY_TRACKER_SUMMARY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addPillTarget, setAddPillTarget] = useState<'water' | 'protein' | 'caffeine' | null>(null);
  const [cheerleads, setCheerleads] = useState<CheerleadNoteRecord[]>([]);
  const [memberFirstName, setMemberFirstName] = useState<string>('');
  const [reflection, setReflection] = useState<WeeklyReflectionRecord | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!currentUser) return;
      try {
        const [loadedSettings, loadedEntry, loadedSummary, loadedCheers, userSnap] = await Promise.all([
          getTrackerSettings(currentUser.uid),
          getDailyEntry(currentUser.uid, dateKey),
          getTrackerSummary(currentUser.uid),
          listCheerleadNotes(currentUser.uid, 10).catch(() => []),
          getDoc(doc(db, 'users', currentUser.uid)).catch(() => null),
        ]);
        setSettings(loadedSettings);
        setEntry(loadedEntry);
        setSummary(loadedSummary);
        setCheerleads(loadedCheers);
        const userData =
          userSnap && (userSnap as any).exists?.() ? ((userSnap as any).data() as Record<string, any>) : {};
        const firstName =
          userData.firstName ||
          (userData.displayName || currentUser.displayName || '').split(/\s+/)[0] ||
          'a friend';
        setMemberFirstName(firstName);
        // Best-effort reconcile of cheerlead counts on each open. Cheap and
        // idempotent, and lets the settings page show accurate counts.
        reconcileCheerleadCounts(currentUser.uid).catch(() => {});

        // Phase 3 correlations — fire-and-forget recompute on every /today
        // open so the /intelligence cards stay fresh without the member
        // having to visit two pages. The service fetches its own 28-day
        // window if we don't pass entries. No UI on /today: spec confirms
        // correlation cards belong on /intelligence with insights, not
        // here next to the log. Wearable observations aren't pre-fetched
        // on /today; the alcohol × HRV card falls back to "waiting on a
        // wearable sync" copy until /intelligence recomputes with them.
        computeAndSaveCorrelations({
          uid: currentUser.uid,
          settings: loadedSettings,
          summary: loadedSummary,
        }).catch((err) => console.error('correlations compute failed', err));

        // Weekly reflection — only relevant inside the visibility window
        // (Sunday eve → end of Wednesday). Try the cached doc first; if it
        // doesn't exist or is stale (no daysLogged), recompute lazily.
        if (shouldShowReflection()) {
          const weekId = reflectionTargetWeekId();
          let cached = await getWeeklyReflection(currentUser.uid, weekId).catch(() => null);
          if (!cached || cached.generatedAt == null) {
            try {
              cached = await computeAndSaveWeeklyReflection({
                uid: currentUser.uid,
                weekId,
                settings: loadedSettings,
                summary: loadedSummary,
              });
            } catch (err) {
              console.error('weekly reflection compute failed', err);
            }
          }
          setReflection(cached);
        }
      } catch (err) {
        console.error('DailyBite load failed', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser, dateKey]);

  const locked = isEntryLocked(entry);

  const save = useCallback(
    async (partial: Partial<DailyEntryRecord>) => {
      if (!currentUser || locked) return;
      setSaving(true);
      setError(null);
      try {
        const updated = await upsertDailyEntryField(currentUser.uid, dateKey, partial, settings);
        setEntry(updated);
        recomputeAndSaveSummary(currentUser.uid, settings)
          .then((nextSummary) => {
            setSummary(nextSummary);
            // Fan out the streak/badge updates to any partner_views the member
            // has invited. Fire-and-forget so it never blocks the save UX.
            if (memberFirstName) {
              syncPartnerViewsFromSummary(currentUser.uid, memberFirstName, nextSummary).catch(
                (err) => console.error('partner view sync failed', err),
              );
            }
          })
          .catch((err) => console.error('summary recompute failed', err));
      } catch (err: any) {
        setError(err?.message || 'Could not save. Try again.');
      } finally {
        setSaving(false);
      }
    },
    [currentUser, dateKey, locked, settings, memberFirstName],
  );

  const dismissCheerlead = async (id: string) => {
    if (!currentUser) return;
    setCheerleads((prev) => prev.filter((c) => c.id !== id));
    try {
      await markCheerleadRead(currentUser.uid, id);
    } catch (err) {
      console.error('mark cheerlead read failed', err);
    }
  };

  const handleDismissReflection = async () => {
    if (!currentUser || !reflection) return;
    setReflection({ ...reflection, dismissedAt: Timestamp.now() });
    try {
      await dismissWeeklyReflection(currentUser.uid, reflection.weekId);
    } catch (err) {
      console.error('dismiss weekly reflection failed', err);
    }
  };

  const completenessPct = useMemo(() => {
    if (!entry) return 0;
    return Math.round((entry.completeness || 0) * 100);
  }, [entry]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const opted = settings.optedIn;
  const unitSystem = settings.unitSystem;

  // ----- Tracker handlers -----
  const setWater = (ml: number) => {
    save({ water: { totalMl: Math.max(0, Math.round(ml)) } });
  };

  const setProtein = (grams: number) => {
    save({ protein: { totalGrams: Math.max(0, Math.round(grams)) } });
  };

  const setCaffeine = (mg: number) => {
    const rounded = Math.max(0, Math.round(mg));
    // Preserve the member's manually-picked lastAt when the slider stays non-zero.
    // Auto-stamp to now only when they cross from 0 → >0 or have no stamp yet.
    const priorLast = entry?.caffeine?.lastAt || null;
    const priorTotal = entry?.caffeine?.totalMg ?? 0;
    let nextLast: Timestamp | null = priorLast;
    if (rounded === 0) {
      nextLast = null;
    } else if (!priorLast || priorTotal === 0 || rounded > priorTotal) {
      nextLast = timestampNow();
    }
    save({ caffeine: { totalMg: rounded, lastAt: nextLast } });
  };

  const setCaffeineLastAt = (ts: Timestamp | null) => {
    if (!entry?.caffeine) return;
    save({ caffeine: { ...entry.caffeine, lastAt: ts } });
  };

  const setAlcohol = (units: number) => {
    const rounded = Math.max(0, Math.round(units * 10) / 10);
    const priorLast = entry?.alcohol?.lastAt || null;
    const priorUnits = entry?.alcohol?.units ?? 0;
    let nextLast: Timestamp | null = priorLast;
    if (rounded === 0) {
      nextLast = null;
    } else if (!priorLast || priorUnits === 0 || rounded > priorUnits) {
      nextLast = timestampNow();
    }
    save({ alcohol: { units: rounded, lastAt: nextLast } });
  };

  const setAlcoholLastAt = (ts: Timestamp | null) => {
    if (!entry?.alcohol) return;
    save({ alcohol: { ...entry.alcohol, lastAt: ts } });
  };

  const setMood = (valence: number, energy: number) => {
    const prevTags = entry?.mood?.tags || [];
    save({ mood: { valence, energy, tags: prevTags, loggedAt: timestampNow() } });
  };

  const toggleMoodTag = (tag: string) => {
    if (!entry?.mood) return;
    const tags = entry.mood.tags.includes(tag)
      ? entry.mood.tags.filter((t) => t !== tag)
      : [...entry.mood.tags, tag];
    save({
      mood: { ...entry.mood, tags, loggedAt: entry.mood.loggedAt || timestampNow() },
    });
  };

  const saveGratitude = (prompt: string, response: string) => {
    save({ gratitude: { prompt, response, loggedAt: timestampNow() } });
  };

  const addMovement = (bristol: number) => {
    const movements: BristolMovement[] = entry?.digestion?.movements || [];
    save({
      digestion: {
        movements: [...movements, { bristol, loggedAt: timestampNow() }],
      },
    });
  };

  const removeLastMovement = () => {
    if (!entry?.digestion?.movements?.length) return;
    const next = entry.digestion.movements.slice(0, -1);
    save({ digestion: { movements: next } });
  };

  const waterMl = entry?.water?.totalMl || 0;
  const proteinG = entry?.protein?.totalGrams || 0;
  const caffeineMg = entry?.caffeine?.totalMg || 0;
  const alcoholUnits = entry?.alcohol?.units || 0;
  const movementCount = entry?.digestion?.movements?.length || 0;

  // ----- Custom pill management -----
  const addCustomPill = async (
    tracker: 'water' | 'protein' | 'caffeine',
    pill: { label: string; amount: number },
  ) => {
    if (!currentUser) return;
    const id = `cp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    const newPill: CustomPill = {
      id,
      label: pill.label,
      amount: pill.amount,
      createdAt: timestampNow(),
    };
    const existing = settings.customPills?.[tracker] || [];
    const nextPills = [...existing, newPill];
    const nextSettings: TrackerSettingsRecord = {
      ...settings,
      customPills: {
        ...(settings.customPills || {}),
        [tracker]: nextPills,
      },
    };
    setSettings(nextSettings);
    try {
      await saveTrackerSettings(currentUser.uid, {
        customPills: nextSettings.customPills,
      });
    } catch (err: any) {
      console.error('save custom pill failed', err);
      setError('Could not save that pill. Try again.');
      setSettings(settings);
    }
  };

  const removeCustomPill = async (
    tracker: 'water' | 'protein' | 'caffeine',
    id: string,
  ) => {
    if (!currentUser) return;
    const existing = settings.customPills?.[tracker] || [];
    const nextPills = existing.filter((p) => p.id !== id);
    const nextSettings: TrackerSettingsRecord = {
      ...settings,
      customPills: {
        ...(settings.customPills || {}),
        [tracker]: nextPills,
      },
    };
    setSettings(nextSettings);
    try {
      await saveTrackerSettings(currentUser.uid, {
        customPills: nextSettings.customPills,
      });
    } catch (err) {
      console.error('remove custom pill failed', err);
      setSettings(settings);
    }
  };

  const totalOpted = Object.values(opted).filter(Boolean).length;

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-10 melio-stagger">
      <style>{SLIDER_STYLE}</style>
      <PageHeader
        breadcrumb={[{ label: 'Home', href: '/home' }, { label: 'Daily Bite' }]}
        eyebrow="Daily Bite"
        title="A few minutes with today."
        subtitle="Small, honest check-ins build the picture your clinician reviews each quarter. Nothing is shared unless you turn it on."
        rightSlot={
          <Link
            to="/today/settings"
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border melio-lift"
            style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white', color: 'var(--melio-ink)' }}
          >
            <SettingsIcon size={16} /> Customise
          </Link>
        }
      />

      <StreakBand summary={summary} />

      <WeeklyReflectionTile reflection={reflection} onDismiss={handleDismissReflection} />

      <CheerleadFeed cheerleads={cheerleads} onDismiss={dismissCheerlead} />

      <div
        className="rounded-2xl border p-4 md:p-5 mb-5 flex items-center gap-4 bg-white"
        style={{ borderColor: 'var(--melio-border)' }}
      >
        <div className="shrink-0">
          <div
            className="rounded-full flex items-center justify-center"
            style={{
              width: 56,
              height: 56,
              background: `conic-gradient(var(--melio-olive-deep) ${completenessPct * 3.6}deg, rgba(79, 93, 24,0.18) 0)`,
            }}
          >
            <div
              className="rounded-full flex items-center justify-center"
              style={{ width: 44, height: 44, backgroundColor: 'white', color: 'var(--melio-ink)', fontWeight: 600 }}
            >
              {completenessPct}%
            </div>
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm" style={{ color: 'var(--melio-ink)' }}>
            <strong>{entry ? `${Math.round(completenessPct)}% of your day logged` : 'Nothing logged yet.'}</strong>
            {saving ? <span className="ml-2" style={{ color: 'var(--melio-text-muted)' }}>Saving…</span> : null}
          </p>
          <p className="text-xs mt-1" style={{ color: 'var(--melio-text-muted)' }}>
            You opted into {totalOpted} of 8 daily trackers.{' '}
            {locked ? 'This day is locked.' : 'You can edit today and the previous 3 days.'}
          </p>
          {error ? (
            <p className="text-xs mt-1" style={{ color: 'var(--melio-terracotta-deep)' }}>{error}</p>
          ) : null}
        </div>
      </div>

      {locked ? (
        <div
          className="rounded-2xl border p-4 mb-5"
          style={{
            borderColor: 'var(--melio-border)',
            backgroundColor: 'rgba(228, 183, 156, 0.18)',
            color: 'var(--melio-ink)',
          }}
        >
          <p className="text-sm">
            <LockIcon size={12} className="inline-block mr-1 -mt-0.5" />
            This day was logged more than 72 hours ago and is now read-only.{' '}
            <Link to="/messages" className="underline" style={{ color: 'var(--melio-olive-deep)' }}>
              Message your care team
            </Link>{' '}
            if you need an edit.
          </p>
        </div>
      ) : null}

      <div className="space-y-4">
        {/* WATER */}
        {opted.water ? (
          <TrackerCard
            tracker="water"
            title="Water"
            summary={waterMl > 0 ? `${formatMl(waterMl, unitSystem)} today` : "Slide to today's hydration"}
            logged={waterMl > 0}
            locked={locked}
          >
            <TrackerSlider
              value={waterMl}
              min={0}
              max={4000}
              step={50}
              onCommit={setWater}
              format={(v) => formatMl(v, unitSystem)}
              leftLabel="Water in millilitres"
              rightLabel="today"
            />
            <PillRow
              items={[
                ...WATER_REFERENCES.map((r) => ({
                  label: r.label,
                  display: formatMl(r.ml, unitSystem),
                  amount: r.ml,
                })),
                ...(settings.customPills?.water || []).map((p) => ({
                  id: p.id,
                  label: p.label,
                  display: formatMl(p.amount, unitSystem),
                  amount: p.amount,
                  isCustom: true,
                })),
              ]}
              onTap={(amount) => setWater(waterMl + amount)}
              onAddCustom={() => setAddPillTarget('water')}
              onRemoveCustom={(id) => removeCustomPill('water', id)}
            />
          </TrackerCard>
        ) : null}

        {/* PROTEIN */}
        {opted.protein ? (
          <TrackerCard
            tracker="protein"
            title="Protein"
            summary={proteinG > 0 ? `${formatGrams(proteinG, unitSystem)} today` : 'Slide to today’s estimate'}
            logged={proteinG > 0}
            locked={locked}
          >
            <TrackerSlider
              value={proteinG}
              min={0}
              max={250}
              step={5}
              onCommit={setProtein}
              format={(v) => formatGrams(v, unitSystem)}
              leftLabel="Protein in grams"
              rightLabel="today"
            />
            <PillRow
              items={[
                ...PROTEIN_REFERENCES.map((r) => ({
                  label: r.label,
                  display: formatGrams(r.grams, unitSystem),
                  amount: r.grams,
                })),
                ...(settings.customPills?.protein || []).map((p) => ({
                  id: p.id,
                  label: p.label,
                  display: formatGrams(p.amount, unitSystem),
                  amount: p.amount,
                  isCustom: true,
                })),
              ]}
              onTap={(amount) => setProtein(proteinG + amount)}
              onAddCustom={() => setAddPillTarget('protein')}
              onRemoveCustom={(id) => removeCustomPill('protein', id)}
            />
          </TrackerCard>
        ) : null}

        {/* CAFFEINE */}
        {opted.caffeine ? (
          <TrackerCard
            tracker="caffeine"
            title="Caffeine"
            summary={
              caffeineMg > 0
                ? entry?.caffeine?.lastAt
                  ? `${caffeineMg} mg · last at ${formatTimeOfDay(entry.caffeine.lastAt)}`
                  : `${caffeineMg} mg today`
                : 'Slide to today’s estimate'
            }
            logged={caffeineMg > 0}
            locked={locked}
          >
            <TrackerSlider
              value={caffeineMg}
              min={0}
              max={600}
              step={5}
              onCommit={setCaffeine}
              format={(v) => `${v} mg`}
              leftLabel="Caffeine in milligrams"
              rightLabel="today"
            />
            {caffeineMg > 0 ? (
              <LastAtRow
                label="Last caffeine at"
                timestamp={entry?.caffeine?.lastAt ?? null}
                onChange={setCaffeineLastAt}
                dateKey={dateKey}
                hint="We use this for the caffeine × sleep correlation once you have enough data."
              />
            ) : null}
            <PillRow
              items={[
                ...CAFFEINE_REFERENCES.map((r) => ({
                  label: r.label,
                  display: `${r.mg} mg`,
                  amount: r.mg,
                })),
                ...(settings.customPills?.caffeine || []).map((p) => ({
                  id: p.id,
                  label: p.label,
                  display: `${p.amount} mg`,
                  amount: p.amount,
                  isCustom: true,
                })),
              ]}
              onTap={(amount) => setCaffeine(caffeineMg + amount)}
              onAddCustom={() => setAddPillTarget('caffeine')}
              onRemoveCustom={(id) => removeCustomPill('caffeine', id)}
            />
          </TrackerCard>
        ) : null}

        {/* ALCOHOL */}
        {opted.alcohol ? (
          <TrackerCard
            tracker="alcohol"
            title="Alcohol"
            summary={
              entry?.alcohol === undefined
                ? 'Slide, or mark today as dry'
                : alcoholUnits === 0
                ? 'Marked as dry today'
                : entry.alcohol.lastAt
                ? `${alcoholUnits.toFixed(1)} units · last at ${formatTimeOfDay(entry.alcohol.lastAt)}`
                : `${alcoholUnits.toFixed(1)} UK units today`
            }
            logged={entry?.alcohol !== undefined}
            locked={locked}
            privacyNote="Not shared with your clinician unless you turn it on."
          >
            <TrackerSlider
              value={alcoholUnits}
              min={0}
              max={15}
              step={0.1}
              onCommit={setAlcohol}
              format={(v) => `${v.toFixed(1)} units`}
              leftLabel="Alcohol in UK units"
              rightLabel="today"
            />
            {alcoholUnits > 0 ? (
              <LastAtRow
                label="Last drink at"
                timestamp={entry?.alcohol?.lastAt ?? null}
                onChange={setAlcoholLastAt}
                dateKey={dateKey}
                hint="Used for the alcohol × HRV / sleep correlations in phase 3."
              />
            ) : null}
            <PillRow
              items={ALCOHOL_REFERENCES.map((r) => ({
                label: r.label,
                display: `${r.units.toFixed(1)} u`,
                amount: r.units,
              }))}
              onTap={(amount) => setAlcohol(alcoholUnits + amount)}
            />
            {entry?.alcohol === undefined ? (
              <button
                type="button"
                onClick={() => setAlcohol(0)}
                className="mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs border"
                style={{
                  borderColor: 'var(--melio-olive-deep)',
                  color: 'var(--melio-olive-deep)',
                  backgroundColor: 'transparent',
                }}
              >
                Mark today as dry
              </button>
            ) : null}
          </TrackerCard>
        ) : null}

        {/* MOOD */}
        {opted.mood ? (
          <TrackerCard
            tracker="mood"
            title="Mood"
            summary={
              entry?.mood?.loggedAt
                ? moodLabelFor(entry.mood.valence, entry.mood.energy)
                : 'Tap a spot that feels like today'
            }
            logged={Boolean(entry?.mood?.loggedAt)}
            locked={locked}
            eyebrow="Evening reflection"
            privacyNote="Not shared with your clinician unless you turn it on."
          >
            <MoodGrid
              valence={entry?.mood?.valence ?? 0}
              energy={entry?.mood?.energy ?? 0}
              hasEntry={Boolean(entry?.mood?.loggedAt)}
              onSelect={setMood}
            />
            <div className="flex flex-wrap gap-2 mt-4">
              {MOOD_TAGS.map((tag) => {
                const active = entry?.mood?.tags?.includes(tag) ?? false;
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleMoodTag(tag)}
                    disabled={!entry?.mood?.loggedAt}
                    className="text-xs rounded-full px-3 py-1 border transition"
                    style={{
                      borderColor: active ? 'var(--melio-olive-deep)' : 'var(--melio-border)',
                      backgroundColor: active ? 'rgba(79, 93, 24,0.15)' : 'transparent',
                      color: active ? 'var(--melio-olive-deep)' : 'var(--melio-text-muted)',
                      cursor: entry?.mood?.loggedAt ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
          </TrackerCard>
        ) : null}

        {/* GRATITUDE */}
        {opted.gratitude ? (
          <GratitudeCard
            locked={locked}
            existing={entry?.gratitude}
            prompt={promptForDate(dateKey)}
            onSave={saveGratitude}
          />
        ) : null}

        {/* DIGESTION */}
        {opted.digestion ? (
          <TrackerCard
            tracker="digestion"
            title="Digestion"
            summary={
              movementCount > 0
                ? `${movementCount} movement${movementCount === 1 ? '' : 's'} today`
                : 'Tap a Bristol type to log a movement'
            }
            logged={movementCount > 0}
            locked={locked}
            privacyNote="Private to you by default — never shared without your say-so."
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {BRISTOL_STOOL.map((stool) => (
                <button
                  key={stool.value}
                  type="button"
                  onClick={() => addMovement(stool.value)}
                  className="text-left rounded-xl border p-3 melio-lift"
                  style={{
                    borderColor: 'var(--melio-border)',
                    backgroundColor: 'var(--melio-cream-warm)',
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium" style={{ color: 'var(--melio-olive-deep)' }}>
                      Type {stool.value}
                    </span>
                    <PlusIcon size={12} />
                  </div>
                  <p className="text-sm mt-1" style={{ color: 'var(--melio-ink)' }}>{stool.label}</p>
                  <p className="text-[11px]" style={{ color: 'var(--melio-text-muted)' }}>{stool.hint}</p>
                </button>
              ))}
            </div>
            {entry?.digestion?.movements?.length ? (
              <button
                type="button"
                onClick={removeLastMovement}
                className="mt-3 text-xs underline"
                style={{ color: 'var(--melio-text-muted)' }}
              >
                Undo last
              </button>
            ) : null}
          </TrackerCard>
        ) : null}

        {/* NICOTINE (behind opt-in) */}
        {opted.nicotine ? (
          <SectionCard eyebrow="Nicotine" title="Form-specific logging coming soon">
            <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
              We keep nicotine separate because the useful dose maths depend on whether you use cigarettes, vapes, pouches, gum, or patches. This tracker is being built — it will let you pick your form(s) first, then log amounts in ways that make sense (e.g. mg on a pouch, cigarettes/day, mL/strength on a vape).
            </p>
          </SectionCard>
        ) : null}

        {/* If everything is opted out */}
        {totalOpted === 0 ? (
          <SectionCard eyebrow="Daily Bite" title="Customise your trackers">
            <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
              You&rsquo;ve opted out of all daily trackers. Head to{' '}
              <Link to="/today/settings" className="underline" style={{ color: 'var(--melio-olive-deep)' }}>
                settings
              </Link>{' '}
              to re-enable the ones you want.
            </p>
          </SectionCard>
        ) : null}
      </div>

      <BadgesSection summary={summary} />

      <div className="mt-6 flex flex-wrap gap-2">
        <ActionChip label="Customise trackers" href="/today/settings" icon={<SettingsIcon size={14} />} />
        <ActionChip label="Back to home" href="/home" icon={<ChevronRightIcon size={12} />} />
      </div>

      {addPillTarget ? (
        <AddPillModal
          tracker={addPillTarget}
          unitSystem={unitSystem}
          onClose={() => setAddPillTarget(null)}
          onSave={async (pill) => {
            await addCustomPill(addPillTarget, pill);
            setAddPillTarget(null);
          }}
        />
      ) : null}
    </div>
  );
}

function MoodGrid({
  valence,
  energy,
  hasEntry,
  onSelect,
}: {
  valence: number;
  energy: number;
  hasEntry: boolean;
  onSelect: (valence: number, energy: number) => void;
}) {
  const size = 260;
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState(false);

  const commitFromEvent = (clientX: number, clientY: number) => {
    const node = gridRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
    const v = (x - 0.5) * 2;
    const en = (0.5 - y) * 2;
    onSelect(Number(v.toFixed(2)), Number(en.toFixed(2)));
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    setDragging(true);
    commitFromEvent(e.clientX, e.clientY);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    commitFromEvent(e.clientX, e.clientY);
  };
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    setDragging(false);
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
  };

  const markerX = ((valence + 1) / 2) * size;
  const markerY = size - ((energy + 1) / 2) * size;

  const Label = ({
    text,
    style,
    muted,
  }: {
    text: string;
    style: React.CSSProperties;
    muted?: boolean;
  }) => (
    <span
      className="absolute text-[10px] uppercase tracking-widest"
      style={{
        ...style,
        color: muted ? 'var(--melio-text-muted)' : 'var(--melio-olive-deep)',
        pointerEvents: 'none',
      }}
    >
      {text}
    </span>
  );

  return (
    <div className="flex items-start gap-4 flex-col md:flex-row">
      <div
        ref={gridRef}
        role="button"
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className="relative rounded-2xl overflow-hidden"
        style={{
          width: size,
          height: size,
          maxWidth: '100%',
          cursor: 'crosshair',
          touchAction: 'none',
          backgroundImage:
            'radial-gradient(circle at 85% 15%, rgba(79, 93, 24,0.40), transparent 55%),' +
            'radial-gradient(circle at 15% 85%, rgba(184,106,74,0.32), transparent 55%),' +
            'radial-gradient(circle at 85% 85%, rgba(47,90,119,0.30), transparent 55%),' +
            'radial-gradient(circle at 15% 15%, rgba(239, 238, 233,0.45), transparent 55%)',
          backgroundColor: 'var(--melio-cream-warm)',
          boxShadow: 'inset 0 0 0 1px var(--melio-border)',
          userSelect: 'none',
        }}
      >
        <div
          className="absolute"
          style={{
            left: '50%',
            top: 0,
            bottom: 0,
            width: 1,
            backgroundColor: 'rgba(31, 42, 30, 0.08)',
            pointerEvents: 'none',
          }}
        />
        <div
          className="absolute"
          style={{
            top: '50%',
            left: 0,
            right: 0,
            height: 1,
            backgroundColor: 'rgba(31, 42, 30, 0.08)',
            pointerEvents: 'none',
          }}
        />
        <Label text="Energised" style={{ top: 8, left: 10 }} />
        <Label text="Pleasant" style={{ top: 8, right: 10 }} />
        <Label text="Low" style={{ bottom: 8, left: 10 }} muted />
        <Label text="Unpleasant" style={{ bottom: 8, right: 10 }} muted />
        {hasEntry ? (
          <div
            className="absolute rounded-full"
            style={{
              width: 22,
              height: 22,
              left: markerX - 11,
              top: markerY - 11,
              backgroundColor: 'var(--melio-olive-deep)',
              boxShadow: '0 0 0 5px rgba(79, 93, 24, 0.22)',
              transition: dragging ? 'none' : 'left 0.15s ease, top 0.15s ease',
              pointerEvents: 'none',
            }}
          />
        ) : null}
      </div>
      <div className="text-xs max-w-[220px]" style={{ color: 'var(--melio-text-muted)' }}>
        <p>
          Tap or drag on the grid. Horizontal = how pleasant today felt. Vertical = how energised.
        </p>
        {hasEntry ? (
          <p className="mt-2">
            Valence <strong>{valence.toFixed(2)}</strong> · Energy <strong>{energy.toFixed(2)}</strong>
          </p>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Weekly reflection tile — Sunday-evening recap of the prior week. Hides if
 * already dismissed; tone is non-judgmental, no scoring. The same content is
 * delivered as a Monday-morning email by the scheduled Cloud Function.
 */
function WeeklyReflectionTile({
  reflection,
  onDismiss,
}: {
  reflection: WeeklyReflectionRecord | null;
  onDismiss: () => void;
}) {
  if (!reflection || reflection.dismissedAt) return null;

  const rangeLabel = reflection.weekStart && reflection.weekEnd
    ? weekRangeLabel(reflection.weekStart, reflection.weekEnd)
    : '';

  return (
    <div
      className="mb-4 rounded-3xl border p-5 md:p-6"
      style={{
        borderColor: 'rgba(79, 93, 24, 0.35)',
        backgroundColor: 'rgba(243, 235, 218, 0.85)',
      }}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p
            className="text-[10px] uppercase tracking-[0.22em] mb-1"
            style={{ color: 'var(--melio-olive-deep)' }}
          >
            Weekly reflection · {rangeLabel}
          </p>
          <p
            className="text-xl md:text-2xl leading-snug"
            style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-ink)' }}
          >
            {reflection.daysLogged} day{reflection.daysLogged === 1 ? '' : 's'} logged
            {reflection.streakAtEnd > 0 ? ` · day ${reflection.streakAtEnd} streak` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss weekly reflection"
          className="shrink-0 rounded-full p-1 text-base"
          style={{ color: 'var(--melio-text-muted)' }}
        >
          ×
        </button>
      </div>

      <p
        className="text-base leading-relaxed mb-3"
        style={{ color: 'var(--melio-ink)' }}
      >
        {reflection.leadParagraph}
      </p>

      {reflection.insights.length > 0 ? (
        <ul className="space-y-2">
          {reflection.insights.map((insight) => (
            <li
              key={insight.id}
              className="rounded-2xl px-3 py-2 text-sm"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                color: 'var(--melio-ink)',
              }}
            >
              {insight.text}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Cheerlead feed — fresh notes from partners. Tap × to dismiss (marks as read
 * server-side). Notes never auto-expire; the member chooses when they're done
 * with each one.
 */
function CheerleadFeed({
  cheerleads,
  onDismiss,
}: {
  cheerleads: CheerleadNoteRecord[];
  onDismiss: (id: string) => void;
}) {
  const unread = cheerleads.filter((c) => !c.read);
  if (unread.length === 0) return null;

  return (
    <div className="mb-4 space-y-2">
      {unread.map((note) => (
        <div
          key={note.id}
          className="rounded-2xl border p-4 flex items-start gap-3"
          style={{
            borderColor: 'rgba(228, 183, 156, 0.45)',
            backgroundColor: 'rgba(228, 183, 156, 0.18)',
          }}
        >
          <div className="min-w-0 flex-1">
            <p
              className="text-[10px] uppercase tracking-[0.2em] mb-1"
              style={{ color: 'var(--melio-terracotta-deep)' }}
            >
              Cheerlead from {note.partnerName}
            </p>
            <p
              className="text-base leading-snug"
              style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-ink)' }}
            >
              "{note.message}"
            </p>
          </div>
          <button
            type="button"
            onClick={() => onDismiss(note.id)}
            aria-label="Dismiss cheerlead"
            className="shrink-0 rounded-full p-1 text-sm"
            style={{ color: 'var(--melio-text-muted)' }}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

function StreakBand({ summary }: { summary: TrackerSummaryRecord }) {
  const hasStreak = summary.currentStreak > 0;
  const freezeDot = (used: boolean) => (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: 999,
        border: '1.5px solid var(--melio-olive-deep)',
        backgroundColor: used ? 'transparent' : 'var(--melio-olive-deep)',
      }}
    />
  );

  return (
    <div
      className="rounded-2xl border p-4 md:p-5 mb-4 flex items-center gap-4 flex-wrap"
      style={{
        borderColor: 'rgba(79, 93, 24, 0.35)',
        backgroundColor: 'rgba(79, 93, 24, 0.10)',
      }}
    >
      <div className="flex items-baseline gap-2">
        <span
          className="text-3xl"
          style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-olive-deep)' }}
        >
          {hasStreak ? `Day ${summary.currentStreak}` : 'Start a streak'}
        </span>
        {summary.longestStreak > summary.currentStreak ? (
          <span className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>
            · longest {summary.longestStreak}
          </span>
        ) : null}
      </div>

      <div className="flex-1 min-w-0" />

      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1">
          {freezeDot(summary.freezesUsedThisMonth >= 1)}
          {freezeDot(summary.freezesUsedThisMonth >= 2)}
        </div>
        <span className="text-xs" style={{ color: 'var(--melio-text-muted)' }}>
          {summary.freezesAvailable === 2
            ? '2 freezes this month'
            : summary.freezesAvailable === 1
            ? '1 freeze left this month'
            : 'no freezes left this month'}
        </span>
      </div>
    </div>
  );
}

function BadgesSection({ summary }: { summary: TrackerSummaryRecord }) {
  const earned = summary.earnedBadges;
  const upcoming = summary.upcomingBadges;

  if (earned.length === 0 && upcoming.length === 0) return null;

  return (
    <div className="mt-8">
      {earned.length > 0 ? (
        <div className="mb-6">
          <p
            className="text-[10px] uppercase tracking-[0.22em] mb-3"
            style={{ color: 'var(--melio-olive-deep)' }}
          >
            Earned
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {earned.map((b) => {
              const def = BADGE_LOOKUP[b.id as BadgeId];
              if (!def) return null;
              return (
                <div
                  key={b.id}
                  className="rounded-2xl border p-4 bg-white"
                  style={{ borderColor: 'var(--melio-border)' }}
                >
                  <div className="flex items-start gap-3">
                    <BadgeGlyph category={def.category} />
                    <div className="min-w-0">
                      <p
                        className="text-base"
                        style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-ink)' }}
                      >
                        {def.label}
                      </p>
                      <p className="text-xs mt-1" style={{ color: 'var(--melio-text-muted)' }}>
                        {def.earnedCopy}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {upcoming.length > 0 ? (
        <div>
          <p
            className="text-[10px] uppercase tracking-[0.22em] mb-3"
            style={{ color: 'var(--melio-olive-deep)' }}
          >
            Next up
          </p>
          <div className="space-y-3">
            {upcoming.map((b) => {
              const def = BADGE_LOOKUP[b.id as BadgeId];
              if (!def) return null;
              const pct = Math.min(100, Math.round((b.currentValue / b.targetValue) * 100));
              return (
                <div
                  key={b.id}
                  className="rounded-2xl border p-4 bg-white"
                  style={{ borderColor: 'var(--melio-border)' }}
                >
                  <div className="flex items-start gap-3">
                    <BadgeGlyph category={def.category} muted />
                    <div className="min-w-0 flex-1">
                      <p
                        className="text-base"
                        style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-ink)' }}
                      >
                        {def.label}
                      </p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--melio-text-muted)' }}>
                        {def.description}
                      </p>
                      <div className="mt-2 h-2 rounded-full" style={{ backgroundColor: 'rgba(31, 42, 30, 0.08)' }}>
                        <div
                          className="h-2 rounded-full"
                          style={{
                            width: `${pct}%`,
                            backgroundColor: 'var(--melio-olive-deep)',
                            transition: 'width 0.25s ease',
                          }}
                        />
                      </div>
                      <p className="text-[11px] mt-1" style={{ color: 'var(--melio-text-muted)' }}>
                        {b.currentValue} / {b.targetValue}
                        {b.hint ? ` · ${b.hint}` : ''}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function BadgeGlyph({
  category,
  muted,
}: {
  category: 'milestone' | 'narrative' | 'identity';
  muted?: boolean;
}) {
  const bg =
    category === 'milestone'
      ? 'rgba(79, 93, 24, 0.18)'
      : category === 'narrative'
      ? 'rgba(228, 183, 156, 0.35)'
      : 'rgba(47, 90, 119, 0.18)';
  const fg =
    category === 'milestone'
      ? 'var(--melio-olive-deep)'
      : category === 'narrative'
      ? 'var(--melio-terracotta-deep)'
      : 'var(--melio-ink)';

  return (
    <span
      className="shrink-0 inline-flex items-center justify-center rounded-full"
      style={{
        width: 36,
        height: 36,
        backgroundColor: muted ? 'rgba(31, 42, 30, 0.06)' : bg,
        color: muted ? 'var(--melio-text-muted)' : fg,
        fontSize: 16,
      }}
    >
      {category === 'milestone' ? '○' : category === 'narrative' ? '◆' : '◇'}
    </span>
  );
}

function GratitudeCard({
  locked,
  existing,
  prompt,
  onSave,
}: {
  locked: boolean;
  existing: DailyEntryRecord['gratitude'] | undefined;
  prompt: string;
  onSave: (prompt: string, response: string) => void;
}) {
  const [draft, setDraft] = useState(existing?.response || '');
  const effectivePrompt = existing?.prompt || prompt;
  const logged = Boolean(existing?.response && existing.response.trim().length > 0);

  useEffect(() => {
    setDraft(existing?.response || '');
  }, [existing?.response]);

  return (
    <TrackerCard
      tracker="gratitude"
      title="Gratitude"
      summary={effectivePrompt}
      logged={logged}
      locked={locked}
      eyebrow="Evening reflection"
      privacyNote="Only visible to you. Never shared, even with your clinician, unless you toggle it on."
    >
      <textarea
        className="w-full rounded-xl border p-3 text-sm"
        style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--melio-cream-warm)', minHeight: 90 }}
        placeholder="A sentence or two is enough."
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
      <div className="flex items-center gap-2 mt-2">
        <button
          type="button"
          onClick={() => onSave(effectivePrompt, draft)}
          disabled={!draft.trim() || draft === existing?.response}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm border"
          style={{
            borderColor: 'var(--melio-olive-deep)',
            color: 'white',
            backgroundColor: 'var(--melio-olive-deep)',
            opacity: !draft.trim() || draft === existing?.response ? 0.5 : 1,
          }}
        >
          {logged ? 'Update reflection' : 'Save reflection'}
        </button>
        {logged && draft !== existing?.response ? (
          <button
            type="button"
            onClick={() => setDraft(existing?.response || '')}
            className="text-xs underline"
            style={{ color: 'var(--melio-text-muted)' }}
          >
            Revert
          </button>
        ) : null}
      </div>
    </TrackerCard>
  );
}
